import {
  isObject,
  isWindowed,
  type Obj,
  operationsProblem,
  ROUTED_ATTRIBUTE,
  valuesProblem,
} from "@/pages/pipelineOperations";
import type { Path, PipelineDocError } from "./pipelineLint";

// The pipeline's `routes` block: each entry well formed and named once, and
// the ids it defines.

/** What a route may match on, as the engine decides it: any attribute for
 *  a route only a windowed pipeline's later stages name, `operation_type`
 *  otherwise. The source filters before the window, so its route is never
 *  the first kind. */
type RouteKeys = "anyAttribute" | "operationType" | "sourceOfWindowed";

/** One entry of the routes block, with where it sits and how errors name
 *  it. */
interface RouteEntry {
  route: Obj;
  label: string;
  path: Path;
  keys: RouteKeys;
}

function routeKeys(doc: Obj, route: Obj): RouteKeys {
  if (!isWindowed(doc)) return "operationType";
  const source = doc.source;
  const filtersSource = isObject(source) && source.route_id === route.id;
  return filtersSource ? "sourceOfWindowed" : "anyAttribute";
}

function routeIdError(
  entry: RouteEntry,
  seen: Set<string>,
): PipelineDocError | null {
  const { route, label, path } = entry;
  const id = route.id;
  if (typeof id !== "string") {
    return { path, message: `${label} is missing "id"` };
  }
  const idPath: Path = [...path, "id"];
  if (id.trim() === "") {
    return { path: idPath, message: `${label} "id" must not be blank` };
  }
  if (seen.has(id)) {
    return {
      path: idPath,
      message: `${label} repeats the id "${id}": route ids must be unique`,
    };
  }
  seen.add(id);
  return null;
}

/** A route that matches on `operation_type` only refuses any other key. */
const operationTypeOnly =
  (why: string) =>
  (key: string): string | null =>
    key === ROUTED_ATTRIBUTE ? null : `matches on "${key}": ${why}`;

/** Why a route of each kind may not match on a key, or null when it may. */
const KEY_RULES: Record<RouteKeys, (key: string) => string | null> = {
  anyAttribute: (key) =>
    key === "" ? "matches on an empty attribute name" : null,
  operationType: operationTypeOnly(
    `a route matches on ${ROUTED_ATTRIBUTE} only`,
  ),
  sourceOfWindowed: operationTypeOnly(
    `the source filters records before the window, so its route matches on ${ROUTED_ATTRIBUTE} only`,
  ),
};

/** What an empty `attributes` of each kind of route should have named. */
const NAMED_BY: Record<RouteKeys, string> = {
  anyAttribute: "an attribute and the values it takes",
  operationType: `${ROUTED_ATTRIBUTE} and the operations it takes`,
  sourceOfWindowed: `${ROUTED_ATTRIBUTE} and the operations it takes`,
};

/** Why the list under `key` is not one, and how the message names the key:
 *  operations under `operation_type`, values under any other attribute. */
function listError(key: string, value: unknown): string | null {
  if (key === ROUTED_ATTRIBUTE) {
    const problem = operationsProblem(value);
    return problem && `${key} ${problem}`;
  }
  const problem = valuesProblem(value);
  return problem && `"${key}" ${problem}`;
}

/** The error for one key of a route's attributes, or null when the route
 *  may match on it and its list is well formed. */
function attributeError(
  entry: RouteEntry,
  key: string,
  value: unknown,
): PipelineDocError | null {
  const path: Path = [...entry.path, "attributes", key];
  const problem = KEY_RULES[entry.keys](key) ?? listError(key, value);
  return problem ? { path, message: `${entry.label} ${problem}` } : null;
}

function attributesErrors(entry: RouteEntry): PipelineDocError[] {
  const { route, label, path } = entry;
  const attributes = route.attributes;
  const attributesPath: Path = [...path, "attributes"];
  if (!("attributes" in route)) {
    return [{ path, message: `${label} is missing "attributes"` }];
  }
  if (!isObject(attributes)) {
    return [
      {
        path: attributesPath,
        message: `${label} "attributes" must be an object`,
      },
    ];
  }
  const keys = Object.keys(attributes);
  if (keys.length === 0) {
    return [
      {
        path: attributesPath,
        message: `${label} "attributes" must name ${NAMED_BY[entry.keys]}`,
      },
    ];
  }
  return keys
    .map((key) => attributeError(entry, key, attributes[key]))
    .filter((e): e is PipelineDocError => e !== null);
}

export function checkRoutes(doc: Obj, errors: PipelineDocError[]): void {
  if (!("routes" in doc)) return;
  if (!Array.isArray(doc.routes)) {
    errors.push({ path: ["routes"], message: '"routes" must be an array' });
    return;
  }
  const seen = new Set<string>();
  doc.routes.forEach((route, i) => {
    const path: Path = ["routes", i];
    const label = `routes[${i}]`;
    if (!isObject(route)) {
      errors.push({ path, message: `${label} must be an object` });
      return;
    }
    const entry = { route, label, path, keys: routeKeys(doc, route) };
    const idError = routeIdError(entry, seen);
    if (idError) errors.push(idError);
    errors.push(...attributesErrors(entry));
  });
}

/** The ids the routes block defines: the only names a stage's `route_id`
 *  can resolve against. An entry that is otherwise malformed still defines
 *  its id, so its own error is not repeated on every stage that names it. */
export function routeBlockIds(doc: Obj): Set<string> {
  const ids = new Set<string>();
  if (!Array.isArray(doc.routes)) return ids;
  for (const route of doc.routes) {
    if (isObject(route) && typeof route.id === "string") ids.add(route.id);
  }
  return ids;
}
